import React, { useMemo } from 'react';
import { Input, Select, DatePicker, Button, Tag } from 'antd';
import {
  SearchOutlined,
  FilterOutlined,
  CloseOutlined,
  FileTextOutlined,
  BankOutlined,
  CalendarOutlined,
} from '@ant-design/icons';
import { motion } from 'framer-motion';
import { Building2 } from 'lucide-react';
import { FilterContainer, FilterTitle, FilterGroup } from '../constants/styles';
import { getTypeColor } from '../constants/fileTypes';

const { RangePicker } = DatePicker;

const DocumentFilters = ({
  documents,
  companies,
  globalSearch,
  setGlobalSearch,
  searchTerm,
  setSearchTerm,
  filteredInfo,
  setFilteredInfo,
  dateFilter,
  setDateFilter,
  dateRangeFilter,
  setDateRangeFilter,
  hasActiveFilters,
}) => {
  // Get unique companies including custom ones
  const getUniqueCompanies = useMemo(() => {
    const uploadedCompanies = documents.map(doc => doc.company);
    const allCompanies = [...new Set([...companies, ...uploadedCompanies])];
    return allCompanies.filter(Boolean).sort();
  }, [documents, companies]);

  // Get unique document types including custom ones
  const getUniqueDocumentTypes = useMemo(() => {
    const uploadedTypes = documents.map(doc => doc.type);
    const allTypes = [...new Set(uploadedTypes)];
    return allTypes.filter(Boolean).sort();
  }, [documents]);

  const clearAllFilters = () => {
    setGlobalSearch('');
    setSearchTerm('');
    setFilteredInfo({});
    setDateFilter(null);
    setDateRangeFilter(null);
  };

  return (
    <FilterContainer>
      <div style={{ 
        display: 'flex', 
        alignItems: 'center', 
        gap: '12px',
        marginBottom: '16px',
        paddingBottom: '16px',
        borderBottom: '1px solid #e2e8f0'
      }}>
        <div style={{
          width: '40px',
          height: '40px',
          borderRadius: '10px',
          background: 'linear-gradient(135deg, #3b82f6 0%, #2563eb 100%)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          boxShadow: '0 4px 12px rgba(59, 130, 246, 0.15)'
        }}>
          <FilterOutlined style={{ fontSize: '18px', color: 'white' }} />
        </div>
        <div>
          <h3 style={{ 
            margin: 0, 
            fontSize: '1rem', 
            fontWeight: '700',
            color: '#1e293b',
            letterSpacing: '-0.01em'
          }}>
            Search & Filter Documents
          </h3>
          <p style={{ 
            margin: 0, 
            fontSize: '0.8125rem', 
            color: '#64748b',
            marginTop: '2px'
          }}>
            Find documents quickly using multiple criteria
          </p>
        </div>
      </div>
      
      <FilterGroup>
        {/* Global Search and Column Filters Row */}
        <div className="search-row">
          {/* Global Search */}
          <Input
            placeholder="Search all fields..."
            prefix={<SearchOutlined style={{ color: '#94a3b8', fontSize: '16px' }} />}
            value={globalSearch}
            onChange={(e) => setGlobalSearch(e.target.value)}
            style={{
              borderRadius: '12px',
              height: '44px',
              border: '2px solid #e2e8f0',
              fontSize: '14px',
              transition: 'all 0.2s ease',
              fontWeight: '500',
            }}
            onFocus={(e) => {
              e.target.style.borderColor = '#3b82f6';
              e.target.style.boxShadow = '0 0 0 3px rgba(59, 130, 246, 0.1)';
            }}
            onBlur={(e) => {
              e.target.style.borderColor = '#e2e8f0';
              e.target.style.boxShadow = 'none';
            }}
          />

          {/* Document Name Search */}
          <Input
            placeholder="Search by name..."
            prefix={<FileTextOutlined style={{ color: '#94a3b8', fontSize: '16px' }} />}
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            style={{
              borderRadius: '12px',
              height: '44px',
              border: '2px solid #e2e8f0',
              fontSize: '14px',
              transition: 'all 0.2s ease',
              fontWeight: '500',
            }}
            onFocus={(e) => {
              e.target.style.borderColor = '#10b981';
              e.target.style.boxShadow = '0 0 0 3px rgba(16, 185, 129, 0.1)';
            }}
            onBlur={(e) => {
              e.target.style.borderColor = '#e2e8f0';
              e.target.style.boxShadow = 'none';
            }}
          />

          {/* Company Multi-Select */}
          <Select
            mode="multiple"
            allowClear
            placeholder="Filter by company..."
            value={filteredInfo.company || []}
            onChange={(value) => setFilteredInfo({ ...filteredInfo, company: value })}
            style={{
              borderRadius: '12px',
              minHeight: '44px',
            }}
            dropdownStyle={{
              borderRadius: '12px',
              boxShadow: '0 10px 25px rgba(0, 0, 0, 0.08)',
            }}
            maxTagCount={2}
            maxTagTextLength={20}
            maxTagPlaceholder={(omittedValues) => `+${omittedValues.length} more`}
            suffixIcon={<Building2 size={16} style={{ color: '#94a3b8' }} />}
            filterOption={(input, option) => {
              const companyName = option?.value || '';
              return companyName.toLowerCase().includes(input.toLowerCase());
            }}
          >
            {getUniqueCompanies.map(company => (
              <Select.Option key={company} value={company}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                  <BankOutlined style={{ color: '#8b5cf6' }} />
                  <span style={{ fontWeight: 500 }}>{company}</span>
                </div>
              </Select.Option>
            ))}
          </Select>

          {/* Document Type Multi-Select */}
          <Select
            mode="multiple"
            allowClear
            placeholder="Filter by type..."
            value={filteredInfo.type || []}
            onChange={(value) => setFilteredInfo({ ...filteredInfo, type: value })}
            style={{
              borderRadius: '12px',
              minHeight: '44px',
            }}
            dropdownStyle={{
              borderRadius: '12px',
              boxShadow: '0 10px 25px rgba(0, 0, 0, 0.08)',
            }}
            maxTagCount={2}
            maxTagTextLength={20}
            maxTagPlaceholder={(omittedValues) => `+${omittedValues.length} more`}
            suffixIcon={<FileTextOutlined style={{ color: '#94a3b8' }} />}
            filterOption={(input, option) => {
              const typeName = option?.value || '';
              return typeName.toLowerCase().includes(input.toLowerCase());
            }}
          >
            {getUniqueDocumentTypes.map(type => (
              <Select.Option key={type} value={type}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                  <Tag 
                    color={getTypeColor(type).bg}
                    style={{ 
                      margin: 0,
                      border: 'none',
                      borderRadius: '6px',
                      color: getTypeColor(type).text,
                      fontWeight: 500,
                    }}
                  >
                    {type.length > 30 ? `${type.substring(0, 30)}...` : type}
                  </Tag>
                </div>
              </Select.Option>
            ))}
          </Select>
        </div>

        {/* Date Filters Row */}
        <div className="search-row">
          {/* Date Range Picker */}
          <RangePicker
            placeholder={['Start date', 'End date']}
            value={dateRangeFilter}
            onChange={setDateRangeFilter}
            style={{
              borderRadius: '12px',
              height: '44px',
              border: '2px solid #e2e8f0',
              width: '100%',
              fontWeight: '500',
            }}
            suffixIcon={<CalendarOutlined style={{ color: '#94a3b8', fontSize: '16px' }} />}
            format="MMM DD, YYYY"
          />

          {/* Clear All Filters Button */}
          {hasActiveFilters && (
            <motion.div
              initial={{ opacity: 0, scale: 0.9 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.2 }}
            >
              <Button
                type="default"
                icon={<CloseOutlined />}
                onClick={clearAllFilters}
                style={{
                  height: '44px',
                  borderRadius: '12px',
                  border: '2px solid #fee2e2',
                  background: '#fef2f2',
                  color: '#ef4444',
                  fontWeight: 600,
                  transition: 'all 0.2s ease',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px',
                  padding: '0 16px',
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.borderColor = '#ef4444';
                  e.currentTarget.style.background = '#fee2e2';
                  e.currentTarget.style.transform = 'translateY(-1px)';
                  e.currentTarget.style.boxShadow = '0 4px 12px rgba(239, 68, 68, 0.15)';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.borderColor = '#fee2e2';
                  e.currentTarget.style.background = '#fef2f2';
                  e.currentTarget.style.transform = 'translateY(0)';
                  e.currentTarget.style.boxShadow = 'none';
                }}
              >
                Clear All Filters
              </Button>
            </motion.div>
          )}
        </div>

        {/* Active Filters Display */}
        {hasActiveFilters && (
          <motion.div
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            style={{
              display: 'flex',
              flexWrap: 'wrap',
              gap: '8px',
              marginTop: '16px',
              padding: '14px 16px',
              background: 'linear-gradient(135deg, #f8fafc 0%, #f1f5f9 100%)',
              borderRadius: '12px',
              border: '1px solid #e2e8f0',
              boxShadow: 'inset 0 2px 4px rgba(0, 0, 0, 0.02)',
            }}
          >
            <span style={{ fontSize: '12px', color: '#64748b', fontWeight: 500 }}>Active filters:</span>
            
            {globalSearch && (
              <Tag
                closable
                onClose={() => setGlobalSearch('')}
                style={{
                  borderRadius: '6px',
                  padding: '2px 8px',
                  background: '#dbeafe',
                  border: 'none',
                  color: '#1e40af',
                }}
              >
                Global: "{globalSearch}"
              </Tag>
            )}
            
            {searchTerm && (
              <Tag
                closable
                onClose={() => setSearchTerm('')}
                style={{
                  borderRadius: '6px',
                  padding: '2px 8px',
                  background: '#d1fae5',
                  border: 'none',
                  color: '#065f46',
                }}
              >
                Name: "{searchTerm}"
              </Tag>
            )}
            
            {filteredInfo.company?.length > 0 && (
              <Tag
                closable
                onClose={() => setFilteredInfo({ ...filteredInfo, company: [] })}
                style={{
                  borderRadius: '6px',
                  padding: '2px 8px',
                  background: '#ede9fe',
                  border: 'none',
                  color: '#5b21b6',
                }}
              >
                {filteredInfo.company.length} companies
              </Tag>
            )}
            
            {filteredInfo.type?.length > 0 && (
              <Tag
                closable
                onClose={() => setFilteredInfo({ ...filteredInfo, type: [] })}
                style={{
                  borderRadius: '6px',
                  padding: '2px 8px',
                  background: '#fef3c7',
                  border: 'none',
                  color: '#92400e',
                }}
              >
                {filteredInfo.type.length} types
              </Tag>
            )}
            
            {dateRangeFilter && (
              <Tag
                closable
                onClose={() => setDateRangeFilter(null)}
                style={{
                  borderRadius: '6px',
                  padding: '2px 8px',
                  background: '#e0e7ff',
                  border: 'none',
                  color: '#3730a3',
                }}
              >
                Range: {dateRangeFilter[0]?.format('MMM DD')} - {dateRangeFilter[1]?.format('MMM DD')}
              </Tag>
            )}
          </motion.div>
        )}
      </FilterGroup>
    </FilterContainer>
  );
};

export default DocumentFilters;